import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, ShieldCheck, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getOnboardingInfo, setOnboardingPassword } from '../lib/api';

export default function Onboard() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOnboardingInfo(token)
      .then((d) => setInfo(d))
      .catch((e) => setErr(e?.response?.data?.detail || 'Invalid onboarding link'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) return toast.error('Password must be at least 6 characters');
    if (pwd !== pwd2) return toast.error('Passwords do not match');
    setBusy(true);
    try {
      await setOnboardingPassword(token, pwd);
      toast.success('Password set. Welcome aboard!');
      navigate('/admin');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set password');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[radial-gradient(ellipse_at_top,#1a1a0e_0%,#0a0a0a_60%)]">
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950/80 backdrop-blur p-6 md:p-8 shadow-2xl" data-testid="onboarding-card">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-zinc-400 py-10"><Loader2 className="animate-spin" size={18} /> Verifying link…</div>
        ) : err ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center text-red-400 mb-3">
              <KeyRound size={20} />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Link Unavailable</h2>
            <p className="text-zinc-400 text-sm" data-testid="onboarding-error">{err}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/30 flex items-center justify-center text-[color:var(--gold)]">
                <ShieldCheck size={22} />
              </div>
              <div>
                <div className="text-zinc-100 font-bold text-lg" data-testid="onboarding-tenant-name">Welcome to {info?.tenant_name}</div>
                <div className="text-zinc-500 text-xs">Set your admin password to get started</div>
              </div>
            </div>

            <div className="text-zinc-400 text-sm mb-5">
              You are setting the password for admin account: <span className="text-[color:var(--gold)] font-bold" data-testid="onboarding-admin-username">{info?.admin_username}</span>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">New Password</label>
                <div className="relative">
                  <Input type={showPwd ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 6 characters" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100 pr-10" data-testid="onboarding-password-input" autoFocus />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                    {showPwd ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Confirm Password</label>
                <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} placeholder="Re-enter password" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="onboarding-password-confirm-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 bg-[color:var(--gold)] hover:brightness-110 text-black font-bold disabled:opacity-60" data-testid="onboarding-submit-btn">
                {busy ? 'Setting…' : 'Set Password & Sign In'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
